import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiAircraftModelAircraftStatus, DigitaiCreateAircraftModelAircraftStatus, DigitaiUpdateAircraftModelAircraftStatusById, DigitaiUpdateAircraftModelsAircraftStatuses } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/aircraft-model-aircraft-status';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AircraftModelAircraftStatusService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiAircraftModelAircraftStatus> | null> = new BehaviorSubject(null);
    aircraftModelAircraftStatusSubject$: BehaviorSubject<DigitaiAircraftModelAircraftStatus | null> = new BehaviorSubject(null);
    aircraftModelsAircraftStatusesSubject$: BehaviorSubject<DigitaiAircraftModelAircraftStatus[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiAircraftModelAircraftStatus> | null>; } = {};
    aircraftModelAircraftStatusScoped: { [key: string]: BehaviorSubject<DigitaiAircraftModelAircraftStatus | null>; } = {};
    aircraftModelsAircraftStatusesScoped: { [key: string]: BehaviorSubject<DigitaiAircraftModelAircraftStatus[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiAircraftModelAircraftStatus>>
    {
        return this.paginationSubject$.asObservable();
    }

    get aircraftModelAircraftStatus$(): Observable<DigitaiAircraftModelAircraftStatus>
    {
        return this.aircraftModelAircraftStatusSubject$.asObservable();
    }

    get aircraftModelsAircraftStatuses$(): Observable<DigitaiAircraftModelAircraftStatus[]>
    {
        return this.aircraftModelsAircraftStatusesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiAircraftModelAircraftStatus>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiAircraftModelAircraftStatus>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftModelAircraftStatus(scope: string, object: DigitaiAircraftModelAircraftStatus): void
    {
        if (this.aircraftModelAircraftStatusScoped[scope])
        {
            this.aircraftModelAircraftStatusScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.aircraftModelAircraftStatusScoped[scope] = new BehaviorSubject(object);
    }

    getScopeAircraftModelAircraftStatus(scope: string): Observable<DigitaiAircraftModelAircraftStatus>
    {
        if (this.aircraftModelAircraftStatusScoped[scope]) return this.aircraftModelAircraftStatusScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftModelsAircraftStatuses(scope: string, objects: DigitaiAircraftModelAircraftStatus[]): void
    {
        if (this.aircraftModelsAircraftStatusesScoped[scope])
        {
            this.aircraftModelsAircraftStatusesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.aircraftModelsAircraftStatusesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeAircraftModelsAircraftStatuses(scope: string): Observable<DigitaiAircraftModelAircraftStatus[]>
    {
        if (this.aircraftModelsAircraftStatusesScoped[scope]) return this.aircraftModelsAircraftStatusesScoped[scope].asObservable();
        return null;
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiAircraftModelAircraftStatus>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiAircraftModelAircraftStatus>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            aircraftModelId = null,
            aircraftStatusId = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            aircraftModelId?: string;
            aircraftStatusId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftModelAircraftStatus;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftModelAircraftStatus;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    aircraftModelId,
                    aircraftStatusId,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModelAircraftStatus(scope, data.object) : this.aircraftModelAircraftStatusSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftModelAircraftStatus;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftModelAircraftStatus;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModelAircraftStatus(scope, data.object) : this.aircraftModelAircraftStatusSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiAircraftModelAircraftStatus[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiAircraftModelAircraftStatus[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModelsAircraftStatuses(scope, data.objects) : this.aircraftModelsAircraftStatusesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateAircraftModelAircraftStatus;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateAircraftModelAircraftStatus[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftModelAircraftStatusById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftModelsAircraftStatuses;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            aircraftModelId = null,
            aircraftStatusId = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            aircraftModelId?: string;
            aircraftStatusId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    aircraftModelId,
                    aircraftStatusId,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
